import { Injectable, OnDestroy, Injector } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, Subject, timer } from 'rxjs';
import { catchError, tap, map, takeUntil, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { 
  LoginRequest, 
  LoginResponse, 
  RefreshTokenRequest, 
  RefreshTokenResponse,
  UserInfo, 
  TokenInfo, 
  SessionState,
  AuthConfig,
  SessionEventType,
  IAuthService
} from '../interfaces/auth.interfaces';

// Re-exportar interfaces que necesitan otros componentes
export { LoginRequest, LoginResponse, UserInfo, TokenInfo, SessionState };
import { InactivityService } from './inactivity.service';
import { AuthorizationService } from './authorization.service';

/**
 * AuthService mejorado con soporte completo para Refresh Tokens e Inactividad
 * Siguiendo principios SOLID:
 * - Single Responsibility: Manejo integral de autenticación
 * - Open/Closed: Extensible para nuevos tipos de autenticación
 * - Liskov Substitution: Implementa IAuthService completamente
 * - Interface Segregation: Usa interfaces específicas
 * - Dependency Inversion: Depende de abstracciones
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService implements IAuthService, OnDestroy {
  
  // ==================== CONFIGURACIÓN ====================
  
  private readonly config: AuthConfig = {
    apiUrl: environment.apiUrl,
    tokenConfig: {
      refreshBufferMinutes: 5,
      maxRefreshAttempts: 3,
      storageKeys: {
        accessToken: 'finance_access_token',
        refreshToken: 'finance_refresh_token',
        userInfo: 'finance_user_info',
        tokenExpiry: 'finance_token_expiry'
      }
    },
    inactivityConfig: {
      inactivityWarningTime: 715, // advertencia 5 min antes del cierre (sesión 12h)
      maxInactivityTime: 720, // 12 horas total antes de cerrar sesión
      warningCountdownTime: 300, // 5 minutos de countdown
      monitoredEvents: ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
    }
  };

  // ==================== ESTADO PRIVADO ====================
  
  private destroy$ = new Subject<void>();
  private isRefreshing = false;
  private refreshAttempts = 0;
  private tokenRefreshTimer: any;

  // Subjects para manejo de estado
  private currentUserSubject = new BehaviorSubject<UserInfo | null>(null);
  private sessionStateSubject = new BehaviorSubject<SessionState>(this.getInitialSessionState());
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  // ==================== OBSERVABLES PÚBLICOS ====================
  
  public currentUser$ = this.currentUserSubject.asObservable();
  public sessionState$ = this.sessionStateSubject.asObservable();

  private _authorizationService: AuthorizationService | null = null;

  constructor(
    private http: HttpClient,
    private router: Router,
    private inactivityService: InactivityService,
    private injector: Injector
  ) {
    console.log('AuthService inicializado con configuración:', this.config);
    this.initializeAuthentication();
    this.setupInactivityHandling();
  }

  private get authorizationService(): AuthorizationService {
    // Lazy loading para evitar dependencias circulares
    if (!this._authorizationService) {
      this._authorizationService = this.injector.get(AuthorizationService);
    }
    return this._authorizationService;
  }

  // ==================== MÉTODOS PÚBLICOS DE AUTENTICACIÓN ====================

  /**
   * Realiza el login del usuario con manejo mejorado de tokens
   */
  login(credentials: LoginRequest): Observable<LoginResponse> {
    const url = `${this.config.apiUrl}/auth/login`;

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    console.log('Iniciando proceso de login para:', credentials.email);

    return this.http.post<LoginResponse>(url, credentials, { headers }).pipe(
      tap(response => {
        this.handleLoginSuccess(response);
        this.startInactivityMonitoring();
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Cierra sesión del usuario con limpieza completa
   */
  async logout(): Promise<void> {
    try {
      console.log('Iniciando proceso de logout...');
      
      const refreshToken = this.getRefreshToken();
      if (refreshToken) {
        // Intentar invalidar el refresh token en el servidor
        const url = `${this.config.apiUrl}/auth/logout`;
        const body = { refreshToken };
        
        await this.http.post(url, body).toPromise().catch(() => {
          // Si falla el logout en servidor, continuar con limpieza local
          console.warn('No se pudo invalidar token en servidor, procediendo con logout local');
        });
      }

      this.clearAuthData();
    } catch (error) {
      console.error('Error durante logout:', error);
      this.clearAuthData(); // Asegurar limpieza incluso si hay error
    }
  }

  /**
   * Cierra sesión localmente sin llamar al servidor
   */
  logoutLocal(): void {
    console.log('Ejecutando logout local...');
    this.clearAuthData();
  }

  /**
   * Verifica si el usuario está autenticado
   */
  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    if (!token) {
      return false;
    }

    return !this.isTokenExpired();
  }

  /**
   * Obtiene el usuario actual
   */
  getCurrentUser(): UserInfo | null {
    return this.currentUserSubject.value;
  }

  /**
   * Obtiene el token de acceso
   */
  getAccessToken(): string | null {
    const storage = this.getStorage();
    return storage.getItem(this.config.tokenConfig.storageKeys.accessToken);
  }

  /**
   * Obtiene el token de refresco
   */
  getRefreshToken(): string | null {
    const storage = this.getStorage();
    return storage.getItem(this.config.tokenConfig.storageKeys.refreshToken);
  }

  /**
   * Renueva el token de acceso usando el refresh token
   */
  refreshAccessToken(): Observable<LoginResponse> {
    const refreshToken = this.getRefreshToken();
    
    if (!refreshToken) {
      console.error('No hay refresh token disponible');
      return throwError(() => new Error('No refresh token available'));
    }

    if (this.isRefreshing) {
      // Si ya se está refrescando, esperar al resultado
      console.log('Refresh ya en progreso, esperando...');
      return this.refreshTokenSubject.pipe(
        takeUntil(this.destroy$),
        switchMap(() => {
          const newToken = this.getAccessToken();
          if (newToken) {
            return new Observable<LoginResponse>(observer => {
              observer.next({
                accessToken: newToken,
                refreshToken: this.getRefreshToken() || '',
                tokenType: 'Bearer',
                expiresIn: this.getRemainingTokenTime()
              });
              observer.complete();
            });
          } else {
            return throwError(() => new Error('Refresh failed'));
          }
        })
      );
    }

    this.isRefreshing = true;
    this.refreshAttempts++;

    const url = `${this.config.apiUrl}/auth/refresh`;
    const currentAccessToken = this.getAccessToken();

    const requestBody: RefreshTokenRequest = {
      refreshToken,
      accessToken: currentAccessToken || undefined
    };

    console.log('Iniciando refresh de token... Intento:', this.refreshAttempts);

    return this.http.post<RefreshTokenResponse>(url, requestBody).pipe(
      tap(response => {
        console.log('Token refresh exitoso');
        this.handleRefreshSuccess(response);
      }),
      catchError(error => {
        console.error('Error en refresh token:', error);
        this.handleRefreshError(error);
        return throwError(() => error);
      }),
      tap(() => {
        this.isRefreshing = false;
      })
    );
  }

  /**
   * Obtiene información del usuario desde el backend
   */
  public fetchCurrentUser(): Observable<UserInfo> {
    const url = `${this.config.apiUrl}/ApplicationUser/me`;
    return this.http.get<any>(url).pipe(
      map(user => {
        const userInfo: UserInfo = {
          id: user.id,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          fullName: user.fullName || `${user.firstName} ${user.lastName}`.trim(),
          roles: user.roles || []
        };

        // Guardar información del usuario
        const storage = this.getStorage();
        storage.setItem(this.config.tokenConfig.storageKeys.userInfo, JSON.stringify(userInfo));
        this.currentUserSubject.next(userInfo);
        this.updateSessionState();

        return userInfo;
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Obtiene el estado actual de la sesión
   */
  getSessionState(): SessionState {
    return this.sessionStateSubject.value;
  }

  /**
   * Obtiene información del token actual
   */
  getTokenInfo(): TokenInfo {
    const token = this.getAccessToken();
    if (!token) {
      return { exists: false, expired: true };
    }

    const isExpired = this.isTokenExpired();
    const expiryTime = this.getTokenExpiryTime();

    return {
      exists: true,
      expired: isExpired,
      expiresAt: expiryTime ? new Date(expiryTime) : undefined,
      remainingMinutes: expiryTime ? Math.max(0, Math.floor((expiryTime - Date.now()) / 1000 / 60)) : 0
    };
  }

  // ==================== MÉTODOS PRIVADOS DE MANEJO DE TOKENS ====================

  /**
   * Maneja el éxito del login con gestión completa de tokens
   */
  private handleLoginSuccess(response: LoginResponse): void {
    console.log('Login exitoso - guardando tokens y configurando sesión...');

    // Limpiar refresh attempts
    this.refreshAttempts = 0;

    // Obtener el storage (siempre localStorage)
    const storage = this.getStorage();

    // Guardar tokens
    storage.setItem(this.config.tokenConfig.storageKeys.accessToken, response.accessToken);
    if (response.refreshToken) {
      storage.setItem(this.config.tokenConfig.storageKeys.refreshToken, response.refreshToken);
      this.refreshTokenSubject.next(response.refreshToken);
    }

    // Calcular y guardar tiempo de expiración
    const expiryTime = Date.now() + (response.expiresIn * 1000);
    storage.setItem(this.config.tokenConfig.storageKeys.tokenExpiry, expiryTime.toString());

    // Actualizar estado de sesión
    this.updateSessionState();

    // Programar refresh automático del token
    this.scheduleTokenRefresh();

    // 🆕 Cargar permisos del usuario desde el token
    this.authorizationService.refreshPermissions();

    console.log('Tokens guardados exitosamente en localStorage. Iniciando obtención de datos del usuario...');
  }

  /**
   * Maneja el éxito del refresh token
   */
  private handleRefreshSuccess(response: RefreshTokenResponse): void {
    const storage = this.getStorage();
    
    console.log('Refresh token exitoso - actualizando tokens...');

    // Limpiar intentos fallidos
    this.refreshAttempts = 0;

    // Actualizar tokens
    storage.setItem(this.config.tokenConfig.storageKeys.accessToken, response.accessToken);
    if (response.refreshToken) {
      storage.setItem(this.config.tokenConfig.storageKeys.refreshToken, response.refreshToken);
      this.refreshTokenSubject.next(response.refreshToken);
    }

    // Actualizar tiempo de expiración
    const expiryTime = Date.now() + (response.expiresIn * 1000);
    storage.setItem(this.config.tokenConfig.storageKeys.tokenExpiry, expiryTime.toString());

    // Actualizar estado de sesión
    this.updateSessionState();

    // 🆕 Recargar permisos después del refresh
    this.authorizationService.refreshPermissions();

    // Reprogramar próximo refresh
    this.scheduleTokenRefresh();

    console.log('Tokens actualizados exitosamente');
  }

  /**
   * Maneja errores en el refresh token
   */
  private handleRefreshError(error: any): void {
    this.isRefreshing = false;

    if (this.refreshAttempts >= this.config.tokenConfig.maxRefreshAttempts) {
      console.error('Máximo de intentos de refresh alcanzado, cerrando sesión');
      this.clearAuthData();
    } else {
      console.warn(`Error en refresh token (intento ${this.refreshAttempts}/${this.config.tokenConfig.maxRefreshAttempts}):`, error);
    }
  }

  /**
   * Programa el próximo refresh automático del token
   */
  private scheduleTokenRefresh(): void {
    // Limpiar timer existente
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
    }

    const expiryTime = this.getTokenExpiryTime();
    if (!expiryTime) {
      return;
    }

    const bufferTime = this.config.tokenConfig.refreshBufferMinutes * 60 * 1000;
    const refreshTime = expiryTime - bufferTime;
    const timeUntilRefresh = refreshTime - Date.now();

    if (timeUntilRefresh > 0) {
      console.log(`Token refresh programado en ${Math.round(timeUntilRefresh / 1000 / 60)} minutos`);
      
      this.tokenRefreshTimer = setTimeout(() => {
        if (this.isAuthenticated() && !this.isRefreshing) {
          console.log('Ejecutando refresh automático del token...');
          this.refreshAccessToken().subscribe({
            error: (error) => console.error('Error en refresh automático:', error)
          });
        }
      }, timeUntilRefresh);
    } else {
      console.warn('Token ya está próximo a expirar, ejecutando refresh inmediato');
      this.refreshAccessToken().subscribe({
        error: (error) => console.error('Error en refresh inmediato:', error)
      });
    }
  }

  // ==================== MÉTODOS DE INICIALIZACIÓN ====================

  /**
   * Inicializa la autenticación cargando datos almacenados
   */
  private initializeAuthentication(): void {
    const storage = this.getStorage();
    const userJson = storage.getItem(this.config.tokenConfig.storageKeys.userInfo);

    console.log('Inicializando autenticación...');

    // Cargar usuario si existe
    if (userJson) {
      try {
        const user = JSON.parse(userJson);
        this.currentUserSubject.next(user);
      } catch (error) {
        console.error('Error al cargar usuario almacenado:', error);
        this.clearStoredData();
      }
    }

    // Si hay token válido pero no hay información de usuario, obtenerla
    if (this.isAuthenticated() && !userJson) {
      console.log('Token válido encontrado, obteniendo información del usuario...');
      this.fetchCurrentUser().subscribe({
        next: () => {
          console.log('Información de usuario cargada exitosamente');
          this.startInactivityMonitoring();
          this.scheduleTokenRefresh();
        },
        error: (error) => {
          console.warn('Error al obtener información del usuario al inicializar:', error);
        }
      });
    } else if (this.isAuthenticated()) {
      // Usuario autenticado con datos completos
      console.log('Usuario autenticado encontrado, iniciando monitoreo');
      this.startInactivityMonitoring();
      this.scheduleTokenRefresh();
    }

    // Actualizar estado inicial
    this.updateSessionState();
  }

  /**
   * Configura el manejo de inactividad
   */
  private setupInactivityHandling(): void {
    // Configurar el servicio de inactividad
    this.inactivityService.updateConfig(this.config.inactivityConfig);

    // Suscribirse a advertencias de inactividad
    this.inactivityService.onInactivityWarning((warningData) => {
      console.log('Advertencia de inactividad recibida:', warningData);
      // Esta advertencia será manejada por el componente modal
    });

    // Suscribirse a expiración de sesión por inactividad
    this.inactivityService.onSessionExpired(() => {
      console.log('Sesión expirada por inactividad');
      this.logoutLocal();
    });

    // Suscribirse a detección de actividad
    this.inactivityService.onActivityDetected(() => {
      this.updateSessionState();
    });
  }

  /**
   * Inicia el monitoreo de inactividad
   */
  private startInactivityMonitoring(): void {
    if (this.isAuthenticated()) {
      this.inactivityService.startMonitoring();
      console.log('Monitoreo de inactividad iniciado');
    }
  }

  /**
   * Detiene el monitoreo de inactividad
   */
  private stopInactivityMonitoring(): void {
    this.inactivityService.stopMonitoring();
    console.log('Monitoreo de inactividad detenido');
  }

  // ==================== MÉTODOS UTILITARIOS ====================

  /**
   * Siempre utiliza localStorage para una mejor experiencia de usuario
   * Esto permite que las sesiones persistan entre pestañas y ventanas
   */
  private getStorage(): Storage {
    return localStorage;
  }

  /**
   * Verifica si el token ha expirado
   */
  private isTokenExpired(): boolean {
    try {
      const storage = this.getStorage();
      const expiryTime = storage.getItem(this.config.tokenConfig.storageKeys.tokenExpiry);

      if (!expiryTime) {
        return true;
      }

      const expiry = parseInt(expiryTime, 10);
      const now = Date.now();

      return now >= expiry;
    } catch (error) {
      console.error('Error al verificar expiración del token:', error);
      return true;
    }
  }

  /**
   * Obtiene el tiempo de expiración del token
   */
  private getTokenExpiryTime(): number | null {
    try {
      const storage = this.getStorage();
      const expiryTime = storage.getItem(this.config.tokenConfig.storageKeys.tokenExpiry);
      return expiryTime ? parseInt(expiryTime, 10) : null;
    } catch (error) {
      console.error('Error al obtener tiempo de expiración:', error);
      return null;
    }
  }

  /**
   * Obtiene el tiempo restante del token en segundos
   */
  private getRemainingTokenTime(): number {
    const expiryTime = this.getTokenExpiryTime();
    if (!expiryTime) return 0;
    
    return Math.max(0, Math.floor((expiryTime - Date.now()) / 1000));
  }

  /**
   * Actualiza el estado de la sesión
   */
  private updateSessionState(): void {
    const isAuthenticated = this.isAuthenticated();
    const tokenInfo = this.getTokenInfo();
    const user = this.getCurrentUser();

    const newState: SessionState = {
      isAuthenticated,
      isTokenValid: tokenInfo.exists && !tokenInfo.expired,
      isTokenExpiring: tokenInfo.remainingMinutes ? tokenInfo.remainingMinutes <= 10 : false,
      remainingTime: tokenInfo.remainingMinutes || 0,
      user,
      lastActivity: new Date()
    };

    this.sessionStateSubject.next(newState);
  }

  /**
   * Obtiene el estado inicial de la sesión
   */
  private getInitialSessionState(): SessionState {
    return {
      isAuthenticated: false,
      isTokenValid: false,
      isTokenExpiring: false,
      remainingTime: 0,
      user: null,
      lastActivity: new Date()
    };
  }

  /**
   * Limpia todos los datos de autenticación
   */
  private clearAuthData(): void {
    console.log('Limpiando datos de autenticación...');
    
    this.stopInactivityMonitoring();
    this.clearStoredData();
    
    // Limpiar timers
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }

    // Resetear estado
    this.isRefreshing = false;
    this.refreshAttempts = 0;
    
    // Actualizar subjects
    this.currentUserSubject.next(null);
    this.refreshTokenSubject.next(null);
    this.updateSessionState();

    // 🆕 Limpiar permisos
    this.authorizationService.clearPermissions();

    // Redirigir al login
    this.router.navigate(['/login']);
  }

  /**
   * Limpia datos almacenados de localStorage
   */
  private clearStoredData(): void {
    const keys = this.config.tokenConfig.storageKeys;

    // Limpiar de localStorage (único storage utilizado)
    localStorage.removeItem(keys.accessToken);
    localStorage.removeItem(keys.refreshToken);
    localStorage.removeItem(keys.userInfo);
    localStorage.removeItem(keys.tokenExpiry);

    // Limpiar cualquier clave legacy de rememberMe si existe
    localStorage.removeItem('finance_remember_me');
    
    // Limpiar de sessionStorage por si hay datos legacy
    sessionStorage.removeItem(keys.accessToken);
    sessionStorage.removeItem(keys.refreshToken);
    sessionStorage.removeItem(keys.userInfo);
    sessionStorage.removeItem(keys.tokenExpiry);
  }

  /**
   * Maneja errores de HTTP
   */
  private handleError = (error: HttpErrorResponse): Observable<never> => {
    let errorMessage = 'Ha ocurrido un error desconocido';

    if (error.error instanceof ErrorEvent) {
      // Error del lado del cliente
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Error del lado del servidor
      switch (error.status) {
        case 400:
          errorMessage = 'Datos de solicitud inválidos';
          break;
        case 401:
          errorMessage = 'No autorizado - Credenciales inválidas';
          break;
        case 403:
          errorMessage = 'Acceso prohibido';
          break;
        case 404:
          errorMessage = 'Recurso no encontrado';
          break;
        case 500:
          errorMessage = 'Error interno del servidor';
          break;
        default:
          errorMessage = `Error del servidor: ${error.status} ${error.message}`;
      }
    }

    console.error('Error en AuthService:', error);
    return throwError(() => error);
  }

  // ==================== CLEANUP ====================

  ngOnDestroy(): void {
    console.log('AuthService destruido');
    this.destroy$.next();
    this.destroy$.complete();
    this.stopInactivityMonitoring();
    
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
    }
  }

  // ==================== MÉTODOS PÚBLICOS ADICIONALES ====================

  /**
   * Extiende manualmente la sesión
   */
  extendSession(): Observable<boolean> {
    return new Observable(observer => {
      if (this.isAuthenticated()) {
        this.inactivityService.extendSession();
        this.updateSessionState();
        observer.next(true);
        observer.complete();
      } else {
        observer.next(false);
        observer.complete();
      }
    });
  }

  /**
   * Obtiene estadísticas de la sesión
   */
  getSessionStats(): any {
    return {
      isAuthenticated: this.isAuthenticated(),
      tokenInfo: this.getTokenInfo(),
      sessionState: this.getSessionState(),
      activityStats: this.inactivityService.getActivityStats(),
      refreshAttempts: this.refreshAttempts,
      isRefreshing: this.isRefreshing
    };
  }
}
